import { useState } from 'react';
import { Download, ReceiptText, RefreshCw, Smartphone } from 'lucide-react';


import PaymentHeader from './payment-details-header';
import PaymentRow from './payment-details-row';
import Button from '@/components/buttons/Button';
import Badge from '@/components/Badge';
import TextInput from '@/components/form-controls/TextInput';

export interface PaymentDetails {
    customerName: string;
    projectName: string;
    referenceNumber: string;
    paymentStatus: string
    amountPaid: string;
    paymentMethod: string;
    phoneNumber: string;
    referenceId: string;
    date: string;
    /** Control-number generation date (ISO string from the API). */
    generatedDate?: string;
    /** Control-number expiry date (ISO string from the API). */
    expiryDate?: string;
    /** Service name being billed (mandatory printout field). */
    billedItem?: string;
    /** Amount in words, currency-prefixed, ending in "ONLY" (mandatory printout field). */
    amountInWords?: string;
    /** User who prepared/generated the bill (mandatory printout field). */
    preparedBy?: string;
}

interface PaymentReceiptProps {
    payment: PaymentDetails;
    /** Control number this bill was issued under — enables print & reuse. */
    controlNumber?: string;
    /** True once the bill has been paid — unlocks the receipt, hides reuse. */
    isPaid?: boolean;
    onDownloadInvoice?: () => void;
    onDownloadReceipt?: () => void;
    onReuse?: (reason: string) => void;
    isReusing?: boolean;
}

export default function PaymentReceipt({
    payment,
    controlNumber,
    isPaid = false,
    onDownloadInvoice,
    onDownloadReceipt,
    onReuse,
    isReusing = false,
}: PaymentReceiptProps) {
    const [showReuse, setShowReuse] = useState(false);
    const [reason, setReason] = useState('');

    const canReuse = !!controlNumber && !isPaid && !!onReuse;

    // Print time is stamped when this view is rendered/printed by the customer.
    const printedOn = new Date().toLocaleString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });

    // Dates arrive as ISO-ish strings ("2026-08-05 14:30:00"); show a friendly
    // form when parseable, otherwise fall back to the raw value.
    const fmtDate = (value?: string): string => {
        if (!value) return '-';
        const parsed = new Date(value.replace(' ', 'T'));
        return isNaN(parsed.getTime())
            ? value
            : parsed.toLocaleString(undefined, {
                  day: 'numeric', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
              });
    };

    return (
        <div>
            <PaymentHeader
                amount={payment.amountPaid}
                date={payment.date}
                reference={payment.referenceNumber}
                isPaid={isPaid}
            />

            <div className=" border border-[#EDE8E4] rounded-md">
                <h2 className="text-lg font-bold text-[#3A3A3A] mb-4 border-b p-2 border-[#EDE8E4]">Payment Details</h2>
                <div className='px-2'>
                    <div className="">
                        <PaymentRow label="Customer name" value={payment.customerName} />
                        <PaymentRow label="Project Name" value={payment.projectName} />
                        {payment.billedItem && (
                            <PaymentRow label="Billed Item" value={payment.billedItem} />
                        )}
                        <PaymentRow label="Reference Number" value={payment.referenceNumber} />
                        {controlNumber && (
                            <PaymentRow label="Control Number" value={controlNumber} />
                        )}
                        {payment.generatedDate && (
                            <PaymentRow label="Generated Date" value={fmtDate(payment.generatedDate)} />
                        )}
                        {payment.expiryDate && (
                            <PaymentRow label="Expiry Date" value={fmtDate(payment.expiryDate)} />
                        )}
                        <PaymentRow
                            label="Payment Status"
                            value={""}
                            icon={
                                <Badge
                                    intent={isPaid ? "success" : "warning"}
                                    text={payment.paymentStatus}
                                />
                            }
                        />
                    </div>

                    <h3 className="text-[#6B5C4F] font-medium mt-2  mb-4">TRANSACTION DETAILS</h3>

                    <div className="space-y-1">
                        <PaymentRow label={isPaid ? "Amount Paid" : "Amount Due"} value={payment.amountPaid} />
                        {payment.amountInWords && (
                            <PaymentRow label="Amount in Words" value={payment.amountInWords} />
                        )}
                        <PaymentRow
                            label="Payment Method"
                            value={payment.paymentMethod}
                            icon={<Smartphone className="w-5 h-5 text-gray-500" />}
                        />
                        <PaymentRow label="Phone Number" value={payment.phoneNumber} />
                        <PaymentRow label="Reference ID" value={payment.referenceId} />
                    </div>

                    <h3 className="text-[#6B5C4F] font-medium mt-2 mb-4">BILL PREPARATION</h3>

                    <div className="space-y-1">
                        <PaymentRow label="Prepared By" value={payment.preparedBy || '-'} />
                        <PaymentRow label="Printed On" value={printedOn} />
                    </div>
                </div>

            </div>

            {/* Printing — invoice/control-number slip is always available once a
                control number exists; the receipt only once the bill is paid. */}
            <div className='flex justify-between w-full mt-3 items-center gap-3'>
                    <Button
                        className='w-full text-primary'
                        leftIcon={<Download  className="w-5 h-5 text-primary" />}
                        intent="secondary"
                        disabled={!controlNumber || !onDownloadInvoice}
                        onClick={onDownloadInvoice}>
                        Download Control Number
                    </Button>
                    <Button
                        className='w-full text-primary'
                        leftIcon={<ReceiptText  className="w-5 h-5 text-primary" />}
                        intent="secondary"
                        disabled={!isPaid || !onDownloadReceipt}
                        onClick={onDownloadReceipt}>
                        Download Receipt
                    </Button>
            </div>

            {/* Reuse — re-activate an expired/cancelled control number so the
                customer can still pay. Hidden once the bill is paid. */}
            {canReuse && (
                <div className='mt-3'>
                    {!showReuse ? (
                        <Button
                            className='w-full text-primary'
                            leftIcon={<RefreshCw className="w-5 h-5 text-primary" />}
                            intent="secondary"
                            onClick={() => setShowReuse(true)}>
                            Reuse Control Number
                        </Button>
                    ) : (
                        <div className='space-y-2 border border-[#EDE8E4] rounded-md p-3'>
                            <p className='text-sm text-[#6B5C4F]'>
                                Re-activate control number <span className='font-medium'>{controlNumber}</span> so you can pay it again.
                            </p>
                            <TextInput
                                label="Reason"
                                placeholder="e.g. Control number expired"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                            <div className='flex justify-end gap-2'>
                                <Button
                                    intent="text"
                                    onClick={() => setShowReuse(false)}
                                    disabled={isReusing}>
                                    Cancel
                                </Button>
                                <Button
                                    intent="primary"
                                    loading={isReusing}
                                    onClick={() => onReuse?.(reason.trim())}>
                                    Reuse
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
